import { Add, Headphones, PlayFilledAlt } from '@carbon/icons-react';
import { AdaptiveWidth, Pad, PadBox, Separator } from 'component/basics';
import { SubtleButton } from 'component/button';
import { colorSurfaceHighlightShade1, colorSurfacePrimary, colorTextPrimary } from 'component/color';
import { DateDisplay } from 'component/date';
import { FormField, ListSelector, RadioGroup, RadioOption } from 'component/form';
import { TextField, UtilityText, WebLinkTextButton } from 'component/text';
import React, { useCallback, useState } from 'react';
import { Image, StyleSheet, View } from 'react-native';
import type { ArticleInfo } from 'structure/article';
import { getArticleInfoAsync } from 'structure/article';
import type { QuestionConfig } from 'structure/question';
import type { Datastore } from 'util/datastore';
import { useDatastore, useGlobalProperty } from 'util/datastore';
import { sortByOrder } from 'util/sort-by-order';
import type { Feature, TLabel } from 'util/stdtypes';
import { declareGlobalProperty, t } from 'util/stdtypes';
import { isValidUrl } from 'util/util';

const RELATED_CONTENT_PAGE_SIZE = 3;
const RELATED_CONTENT_INITIAL_COUNT = 3;

export type ContentType = 'audio' | 'media' | 'text';

export type RelatedContent = ArticleInfo & {
    url: string;
    type: ContentType;
};

export const RelatedContentFeature: Feature<QuestionConfig, {}> = {
    key: 'related-content',
    name: 'Related Content',
    providesConfig: {
        bottomRecircWidgets: [RelatedContent],
    },
};

const relatedContentProperty = declareGlobalProperty<RelatedContent[]>('relatedContent');

export function RelatedContent() {
    const s = RelatedContentStyle;
    const relatedContent = useGlobalProperty(relatedContentProperty);
    const [visibleCount, setVisibleCount] = useState(RELATED_CONTENT_INITIAL_COUNT);

    function showMore() {
        setVisibleCount((prevCount) => prevCount + RELATED_CONTENT_PAGE_SIZE);
    }

    if (!relatedContent || relatedContent.length === 0) {
        return null;
    }

    const shownContent = relatedContent.slice(0, visibleCount);
    const hasMore = visibleCount < relatedContent.length;

    return (
        <View style={s.outer}>
            <PadBox horiz={32} vert={32}>
                <UtilityText label={t('Learn more about this topic')} />
                <Pad size={12} />
                {shownContent.map((content, i) => (
                    <ContentPreview content={content} first={i === 0} key={content.url} />
                ))}
                {hasMore && (
                    <>
                        <Pad size={32} />
                        <SubtleButton color={colorTextPrimary} icon={Add} label={t('Show more')} onPress={showMore} padRight />
                    </>
                )}
            </PadBox>
        </View>
    );
}
const RelatedContentStyle = StyleSheet.create({
    outer: {
        backgroundColor: colorSurfaceHighlightShade1,
    },
});

function ContentIcon({ type }) {
    switch (type) {
        case 'audio':
            return <Headphones height={16} width={16} />;
        case 'media':
            return <PlayFilledAlt height={16} width={16} />;
        default:
            return null;
    }
}

type ContentPreviewProps = {
    first: boolean;
    content: RelatedContent;
};

export function ContentPreview({ first, content }: ContentPreviewProps) {
    const s = ContentPreviewStyle;
    const [narrow, setNarrow] = useState(false);

    let time: number | undefined;
    if (content.date) {
        time = new Date(content.date).getTime();
    }

    return (
        <AdaptiveWidth breakpoint={400} setNarrow={setNarrow}>
            {!first && (
                <PadBox top={20}>
                    <Separator />
                </PadBox>
            )}
            <View style={s.outer}>
                <View style={s.imageWrapper}>
                    {content.type && content.type !== 'text' && (
                        <View style={s.iconWrapper}>
                            <ContentIcon type={content.type} />
                        </View>
                    )}
                    <Image
                        onError={(e) => console.warn('Failed to load article image:', content?.image)}
                        source={{ uri: content?.image }}
                        style={[s.image, narrow ? { width: 70 } : {}]}
                    />
                </View>
                <View style={s.right}>
                    <WebLinkTextButton
                        color={colorTextPrimary}
                        heading
                        linkType='related-content-click'
                        numberOfLines={2}
                        text={content?.title}
                        type='small'
                        url={content.url}
                        weight='strong'
                    />
                    <Pad size={8} />
                    {time && <DateDisplay color={colorTextPrimary} timestamp={time} />}
                </View>
            </View>
        </AdaptiveWidth>
    );
}

const ContentPreviewStyle = StyleSheet.create({
    outer: {
        flexDirection: 'row',
        marginTop: 20,
    },
    imageWrapper: {
        overflow: 'hidden',
        position: 'relative',
        borderRadius: 8,
    },
    image: {
        width: 100,
        height: 70,
        borderRadius: 8,
    },
    right: {
        marginLeft: 20,
        flexShrink: 1,
    },
    iconWrapper: {
        zIndex: 2,
        position: 'absolute',
        bottom: 8,
        left: 8,
        justifyContent: 'center',
        alignItems: 'center',
        width: 24,
        height: 24,
        borderRadius: 1000,
        backgroundColor: colorSurfacePrimary,
    },
});

type AddItem = {
    url: string;
    type: ContentType;
};

export function RelatedContentEditScreenWidget() {
    const relatedContent: RelatedContent[] = useGlobalProperty(relatedContentProperty) ?? [];
    const datastore = useDatastore();

    async function onAddItems(items: AddItem[]) {
        const expandedItems: RelatedContent[] = [];
        for (const item of items) {
            if (!isValidUrl(item.url)) {
                return { error: t('Invalid URL format. Please enter a valid URL.') };
            }
            const articleInfo = await getArticleInfoAsync(datastore, item.url);
            if (!articleInfo) {
                return { error: t('Unable to retrieve article information. Please check the URL and try again.') };
            }
            expandedItems.push({ ...articleInfo, url: item.url, type: item.type });
        }
        datastore.setGlobalProperty(relatedContentProperty, [...relatedContent, ...expandedItems]);
        return { error: null };
    }
    function onDeleteItem(keys: string) {
        datastore.setGlobalProperty(
            relatedContentProperty,
            relatedContent.filter((item) => !keys.includes(item.url)),
        );
    }
    function onChangeSortOrder(orderedKeys: string[]) {
        const sortedItems = sortByOrder(relatedContent, orderedKeys, (item) => item.url);
        datastore.setGlobalProperty(relatedContentProperty, sortedItems);
    }
    const items = relatedContent?.map((item) => ({ key: item.url, text: item.title })) ?? [];
    return (
        <ListSelector
            addLabel={t('Add content')}
            addWidget={AddRelatedContent}
            items={items}
            label={t('Related content')}
            onAddItems={onAddItems}
            onChangeSortOrder={onChangeSortOrder}
            onDeleteItem={onDeleteItem}
            reorderable
        />
    );
}

type AddRelatedContentProps = {
    setToAdd: (items: AddItem[]) => void;
};

function AddRelatedContent({ setToAdd }: AddRelatedContentProps) {
    const [url, setUrl] = useState('');
    const [type, setType] = useState<ContentType>('text');

    function updateToAdd(newUrl: string, newType: ContentType) {
        if (newUrl) {
            setToAdd([{ url: newUrl, type: newType }]);
        } else {
            setToAdd([]);
        }
    }

    return (
        <View>
            <FormField label={t('Story URL')}>
                <TextField
                    autoFocus
                    multiline={false}
                    onChange={(newUrl) => {
                        setUrl(newUrl);
                        updateToAdd(newUrl, type);
                    }}
                    placeholderLabel={t('https://www.example.com')}
                    testID='story-url'
                    value={url}
                />
            </FormField>
            <Pad size={24} />
            <FormField label={t('Story format')}>
                <RadioGroup
                    onChange={(newType) => {
                        const contentType = newType as ContentType;
                        setType(contentType);
                        updateToAdd(url, contentType);
                    }}
                    value={type}
                >
                    <RadioOption label={t('Article')} radioKey='text' />
                    <RadioOption label={t('Video')} radioKey='media' />
                    <RadioOption label={t('Audio')} radioKey='audio' />
                </RadioGroup>
            </FormField>
        </View>
    );
}
